import {
  MetricRequestCareArticleEngagement,
  MetricRequestLearnArticleEngagement,
  MetricRequestLessonEngagement,
  MetricRequestLessonViewCount,
  MetricRequestQueryEngagement,
  MetricRequestQuestionnaireEngagement,
  MetricRequestQuizEngagement,
  MetricRequestTipEngagement,
  MetricRequestTotalEngagement,
  Nullish,
} from 'types'
import { TOKEN } from 'utils'
import NotificationSys from '../components/NotificationSystem'
import { getBaseURL, httpHeaders, isDevEnv } from './axios'

// Right now we have a problem for sending metrics after logout.
// Token is stored in localStorage, and we clear it after logout.
// And metrics get token from localStorage before these changes, so we get 401.
// Here we store token in memory, and we don't clear it after logout, so we can send metrics after logout.
// But because token is in memory and will be cleared after refresh
// and time of token is around 15min, it's not a big security issue.
class MetricsClient {
  private token: Nullish<string> = localStorage.getItem(TOKEN)

  setToken(token: string): void {
    this.token = token
  }

  getToken(): Nullish<string> {
    return this.token
  }

  clearToken(): void {
    this.token = null
  }
}

export const metricsClient = new MetricsClient()

const prepareHeaders = () => {
  const token = metricsClient.getToken()
  const headers: HeadersInit = {
    ...httpHeaders,
    authorization: `Bearer ${token}`,
  }
  return headers
}

export const addLessonViewMetric = async (request: MetricRequestLessonViewCount) => {
  fetch(getBaseURL() + '/metrics/lesson-view', {
    keepalive: true,
    method: 'POST',
    headers: prepareHeaders(),
    body: JSON.stringify(request),
  })
}

export const addLessonEngagementMetric = (request: MetricRequestLessonEngagement) => {
  try {
    return fetch(getBaseURL() + '/metrics/lesson-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: lesson-engagement,
        <br />
        User ID: {request.userId},
        <br />
        lessonUuid: {request.lessonUuid},
        <br />
        lessonId: {request.lessonId},
        <br />
        courseId: {request.courseId},
        <br />
        scope: {request.scope},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}

export const addLearnArticleEngagementMetric = (request: MetricRequestLearnArticleEngagement) => {
  try {
    return fetch(getBaseURL() + '/metrics/learn-article-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: learn-article-engagement,
        <br />
        User ID: {request.userId},
        <br />
        learnArticleId: {request.learnArticleId},
        <br />
        learnTopicId: {request.learnTopicId},
        <br />
        scope: {request.scope},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}
export const addQueryEngagementMetric = (request: MetricRequestQueryEngagement) => {
  try {
    return fetch(getBaseURL() + '/metrics/query-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: query-engagement,
        <br />
        User ID: {request.userId},
        <br />
        queryId: {request.queryId},
        <br />
        learnArticleId: {request?.learnArticleId || '-'},
        <br />
        scope: {request.scope},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}

export const addCareArticleEngagementMetric = (request: MetricRequestCareArticleEngagement) => {
  try {
    return fetch(getBaseURL() + '/metrics/care-article-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: care-article-engagement,
        <br />
        User ID: {request.userId},
        <br />
        careArticleId: {request.careArticleId},
        <br />
        scope: {request.scope},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}

export const addTipEngagementMetric = (request: MetricRequestTipEngagement) => {
  try {
    return fetch(getBaseURL() + '/metrics/tip-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: tip-engagement,
        <br />
        tipId: {request.tipId},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}

export const addQuestionnaireEngagementMetric = (request: MetricRequestQuestionnaireEngagement) => {
  try {
    return fetch(getBaseURL() + '/metrics/questionnaire-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: questionnaire-engagement,
        <br />
        User ID: {request.userId},
        <br />
        questionnaireId: {request.questionnaireId},
        <br />
        scope: {request.scope},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}

export const addQuizEngagementMetric = (request: MetricRequestQuizEngagement) => {
  try {
    return fetch(getBaseURL() + '/metrics/quiz-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: quiz-engagement,
        <br />
        User ID: {request.userId},
        <br />
        quizId: {request.quizId},
        <br />
        scope: {request.scope},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}

export const addTotalEngagementMetric = (request: MetricRequestTotalEngagement) => {
  try {
    fetch(getBaseURL() + '/metrics/total-engagement', {
      keepalive: true,
      method: 'POST',
      headers: prepareHeaders(),
      body: JSON.stringify(request),
    })
  } catch (e) {
    if (!isDevEnv) return
    NotificationSys.showWarning(
      <>
        {'Error while sending metrics'}
        <br />
        Metric type: total-engagement,
        <br />
        User ID: {request.userId},
        <br />
        Engaged from: {request.engagedFrom},
        <br />
        Engaged to: {request.engagedTo}
      </>,
      50000,
    )
  }
}
